import { compareDesc } from 'date-fns';
import { atom } from 'jotai';
import { groupByDay } from '../util/groupByDay';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { uniqBy } from '../util/arrayUtils';
import { AssetState, Notification } from '../util/Events/Messages';

export enum SaveResult {
  NONE,
  SUCCESS,
  FAIL,
}

export type SaveAction = '' | 'Save' | 'Delete';

export type SaveNotification = {
  id: SaveResult;
  action: SaveAction;
  message?: string;
};

export type NotificationState = {
  asset: AssetState;
  origin: 'Notifications';
};

export const LIVE_NOTIFICATIONS = atom<Notification[]>([]);

export const SAVE_NOTIFICATION = atom<SaveNotification>({
  id: SaveResult.NONE,
  action: '',
  message: '',
});

export const REPLAY_NOTIFICATIONS = atom<Notification[]>([]);

export const SEEN_NOTIFICATIONS = atomWithStorage<{ [id: string]: boolean | undefined }>(
  '@geomoby/notifications',
  {},
  createJSONStorage(() => localStorage),
);
export const SEEN_REPLAY_NOTIFICATIONS = atomWithStorage<{ [id: string]: boolean | undefined }>(
  '@geomoby/notifications',
  {},
  createJSONStorage(() => sessionStorage),
);

export const NOTIFICATIONS = atom<Notification[]>(get =>
  [
    ...get(LIVE_NOTIFICATIONS).map(notification => ({
      ...notification,
      seen: !!get(SEEN_NOTIFICATIONS)[notification.id],
      replayed: false,
    })),
    ...get(REPLAY_NOTIFICATIONS).map(notification => ({
      ...notification,
      seen: !!get(SEEN_REPLAY_NOTIFICATIONS)[notification.id],
      replayed: true,
    })),
  ].sort(({ date: a }, { date: b }) => compareDesc(a, b)),
);

export const LATEST_UNSEEN_REPLAY_NOTIFICATION = atom<Notification | undefined>(get =>
  get(NOTIFICATIONS).filter(({ replayed }) => replayed)[0]?.seen
    ? undefined
    : get(NOTIFICATIONS).filter(({ replayed }) => replayed)[0],
);

export const LATEST_UNSEEN_NOTIFICATION = atom<Notification | undefined>(get =>
  get(NOTIFICATIONS)[0]?.seen ? undefined : get(NOTIFICATIONS)[0],
);

export const NOTIFICATIONS_GROUPED_BY_DAY = atom(get =>
  get(NOTIFICATIONS)
    .filter(n => !n.replayed)
    .reduce<Notification[][]>(groupByDay, []),
);

export const REPLAY_NOTIFICATIONS_GROUPED_BY_DAY = atom(get => {
  const allNotifications = get(NOTIFICATIONS);
  const replayNotifications = allNotifications.filter(n => n.replayed);
  const liveNotifications = allNotifications.filter(n => !n.replayed);
  // Want all replay notifications, and live notifications that have not been replayed
  const wantedNotifications = uniqBy([...replayNotifications, ...liveNotifications], 'id').sort(
    ({ date: a }, { date: b }) => compareDesc(a, b),
  );
  return wantedNotifications.reduce<Notification[][]>(groupByDay, []);
});
